import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeProvider';
import { FilterBar } from './FilterBar';
import styles from './project-tabs/DocumentsTab.module.css';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useToast } from '../context/ToastProvider';
import { useKnowledgeDocuments, KnowledgeDocument } from '../hooks/useKnowledgeDocuments';
import GlobalEmptyState from './GlobalEmptyState';
import DoubleConfirmationModal from './DoubleConfirmationModal';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { DocumentTile, ProjectDocumentData } from './DocumentTile';

interface KnowledgeDocumentsTabProps {
  knowledgeId: string;
  onAddDocument: () => void;
}

export const KnowledgeDocumentsTab: React.FC<KnowledgeDocumentsTabProps> = ({ knowledgeId, onAddDocument }) => {
  const { t } = useLocale();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const showToast = useToast();
  const navigate = useNavigate();
  const { documents, loading } = useKnowledgeDocuments(knowledgeId);
  const [searchValue, setSearchValue] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<KnowledgeDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (documentOrId: KnowledgeDocument | string) => {
    const d = typeof documentOrId === 'string'
      ? documents.find((x) => x.id === documentOrId)
      : documentOrId;
    if (d) {
      setDocumentToDelete(d);
      setShowDeleteModal(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      const docRef = doc(db, 'knowledge', knowledgeId, 'documents', documentToDelete.id);
      await deleteDoc(docRef);
      showToast(t('project_tab_delete_success') || 'Document deleted successfully', 'success');
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting knowledge document:', error);
      showToast(t('project_tab_delete_error') || 'Failed to delete document', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  const filteredDocuments = documents.filter((d) =>
    searchValue === '' || d.filename.toLowerCase().includes(searchValue.toLowerCase())
  );

  const convertToProjectDocumentData = (d: KnowledgeDocument): ProjectDocumentData => ({
    id: d.id,
    documentId: d.documentId,
    filename: d.filename,
    fileUrl: d.fileUrl,
    storagePath: d.storagePath,
    contentType: d.contentType,
    status: d.status,
    createdAt: d.createdAt,
    projectId: knowledgeId,
    userId: d.userId,
    embeddingEnabled: d.embeddingEnabled,
    embeddingSucceeded: d.embeddingSucceeded,
    chunksCreated: d.chunksCreated,
    embeddingError: d.embeddingError,
  });

  return (
    <div className={styles.documentsTab}>
      {/* FilterBar - no status filter, search + add button */}
      <FilterBar
        filters={[]}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t('project_tab_search_documents') || 'Search documents...'}
        showInviteButton={true}
        onAddUser={onAddDocument}
        addButtonText={t('project_tab_add_document') || 'Add a document'}
      />

      {loading ? (
        <div className={styles.loading}>Loading documents...</div>
      ) : filteredDocuments.length === 0 ? (
        <GlobalEmptyState
          buttonText={t('company_page_add_first_document') || t('project_tab_add_document') || 'Add first document'}
          onButtonClick={onAddDocument}
          buttonIcon={
            <img
              src={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
              alt="Add"
              width={12}
              height={12}
            />
          }
        />
      ) : (
        <div className={styles.documentsGrid} style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          {filteredDocuments.map((document) => (
            <DocumentTile
              key={document.id}
              document={convertToProjectDocumentData(document)}
              variant="tile"
              isProjectDocument={true}
              onClick={(id) => navigate(`/document/${id}?knowledgeId=${knowledgeId}`)}
              onView={(id) => navigate(`/document/${id}?knowledgeId=${knowledgeId}`)}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('project_tab_delete_document') || 'Delete Document'}
        paragraph={
          documentToDelete
            ? t('project_tab_delete_document_confirm') ||
              `Are you sure you want to delete "${documentToDelete.filename}"? This action cannot be undone.`
            : ''
        }
        primaryLabel={t('project_tab_delete') || 'Delete'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('project_tab_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={handleDeleteConfirm}
        onSecondary={handleDeleteCancel}
        primaryDestructive
        loading={isDeleting}
      />
    </div>
  );
};
